import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { AudioService } from '../../services/audio.service';
import { HttpJsonService } from '../../services/http-json.service';
import { ToastController } from '@ionic/angular';
import { trigger, style, animate, transition, group, query, animateChild } from '@angular/animations';

export enum CountdownMode {
  Starting = 1,
  Workout = 2,
  Rest = 3,
  Warning = 4,
  Stop = 5,
}

export interface CountdownTimer {
  seconds: number;
  secondsRemaining: number;
  runTimer: boolean;
  hasStarted: boolean;
  hasFinished: boolean;
  displayTime: string;
}

@Component({
  selector: 'countdown-timer',
  templateUrl: './countdown-timer.component.html',
  styleUrls: ['./countdown-timer.component.scss'],

  animations: [
    trigger('container', [
      transition(':enter', [
        style({ opacity: '0' }),
        group([
          animate('1500ms ease-out', style({ opacity: '1' })),
          // query('@badge, @message', [
          query('@message', [
            animateChild()
          ])
        ])

      ]),
      transition(':leave', [
        group([
          animate('1500ms ease-out', style({ opacity: '0' })),
          // query('@badge, @message', [
          query('@message', [
            animateChild()
          ])
        ])
      ])
    ]),
    trigger('message', [
      transition(':enter', [
        style({ opacity: '0' }),
        animate('500ms 1000ms ease-out', style({ opacity: '1' }))
      ]),
      transition(':leave', [
        animate('1500ms ease-in', style({ opacity: '0' }))
      ])
    ])

  ]

})
export class CountdownTimerComponent implements OnInit {


  /** show or not show the 'Round 1 of X' text */
  @Input() hideUI:boolean;
  @Input() showRoundsText;
  @Input() startCountdownInSecs: number = 30;

  @Input() startCountdownSoundsInSecsAt: string;
  startCountdownSoundsAt: number[];

  @Input() roundTimeInSeconds: number;

  timeInSeconds: number;

  /** use string like '30, 60, 90' - will play a 'status' sound at those times, during the workout phase */
  @Input() roundStatusSoundsInSecsAt: string;
  roundStatusSoundsAt: number[];

  /** use string like '120' - will play a 'status' sound at those times, during the workout phase - used to signify the end of the round is close */
  @Input() roundWarningSoundsInSecsAt: string;
  roundWarningSoundsAt: number[];

  /** use string like '20' - will play a 'warning' sound at those times, during the rest phase - used to signify the end of the rest phase is close */
  @Input() restWarningSoundsInSecsAt: string;
  restWarningSoundsAt: number[];

  @Input() roundEndingWarningAt: number;
  @Input() restEndingWarnAt: number;
  @Input() restInSeconds: number;
  @Input() rounds: number = 0;
  @Input() workoutMessage: string = "Workout";
  @Input() restMessage: string = "Rest";
  @Input() warnMessage: string = "Get ready";
  @Input() finishedMessage: string = "Done";
  @Input() startingMessage: string = this.warnMessage;

  @Input() restEveryXRounds: number;
  @Input() restEveryXRoundsFor: number;

  @Output() onTimerCompleted = new EventEmitter();
  @Output() onStartCountdown = new EventEmitter();

  @Output() onRestStarted = new EventEmitter();

  @Output() onRoundDone = new EventEmitter();
  @Output() onRoundStarted = new EventEmitter();
  @Output() onRoundEndingWarn = new EventEmitter();
  @Output() onWarnStarted = new EventEmitter();
  @Output() onCountdownModeChange = new EventEmitter();

  @Output() onExit = new EventEmitter();


  cssClass = "workout-round";
  displayLabel: string = "";
  roundCount = 0;
  workoutMode: CountdownMode = CountdownMode.Starting;
  timer: CountdownTimer;

  paused: boolean = false;

  private increment;
  private transform;
  private percent;
  private fixTransform;

  constructor(private sanitizer: DomSanitizer, private audio: AudioService, private api: HttpJsonService, public toastCtrl: ToastController) { }

  ngAfterViewInit() {
    console.log('countdown-timer after view init');
    // this.audio.preload('start-round', 'assets/audio/start-round.mp3');
    // this.audio.preload('round-start', 'assets/audio/bell.mp3');
    this.audio.preload('round-start', 'assets/audio/start-round.mp3');
    this.audio.preload('round-status', 'assets/audio/slap.mp3');
    this.audio.preload('round-warning', 'assets/audio/warning_whistle.mp3');
    this.audio.preload('round-end', 'assets/audio/end-round.mp3');
    this.audio.preload('warning', 'assets/audio/warning.mp3');
    this.audio.preload('rest-warning', 'assets/audio/warning.mp3');

    this.startTimer(CountdownMode.Starting);
  }

  ngOnInit() {
    console.log('countdown-timer on init');

    this.startCountdownSoundsAt = this.api.convertCSVStringToNumberArray(this.startCountdownSoundsInSecsAt);
    this.roundStatusSoundsAt = this.api.convertCSVStringToNumberArray(this.roundStatusSoundsInSecsAt);
    this.roundWarningSoundsAt = this.api.convertCSVStringToNumberArray(this.roundWarningSoundsInSecsAt);
    this.restWarningSoundsAt = this.api.convertCSVStringToNumberArray(this.restWarningSoundsInSecsAt);

    console.log('round status sounds at: ' + this.roundStatusSoundsAt);
    console.log('round warning sounds at: ' + this.roundWarningSoundsAt);
    console.log('rest warning sounds at: ' + this.restWarningSoundsAt);

    this.timeInSeconds = this.startCountdownInSecs;
    this.cssClass = 'warning';
    this.displayLabel = this.warnMessage;
    this.initTimer();

    // this.startTimer();
  }

  exit() {
    this.onExit.emit({ rounds: this.roundCount });
  }

  restartTimer() {
    this.roundCount = 0;
    this.timer.hasFinished = true;
    this.timeInSeconds = this.startCountdownInSecs;
    this.cssClass = 'warning';
    this.displayLabel = this.warnMessage;
    this.initTimer();
    this.startTimer(CountdownMode.Starting);
  }

  hasFinished() {
    return this.timer.hasFinished;
  }

  initProgressBar() {
    this.percent = 100;
    this.increment = 180 / 100;
    const progress = 'rotate(' + this.increment * this.percent + 'deg)';
    this.transform = this.sanitizer.bypassSecurityTrustStyle(progress);
    this.fixTransform = this.sanitizer.bypassSecurityTrustStyle(progress);
  }

  initTimer() {
    this.initProgressBar();
    if (!this.timeInSeconds) { this.timeInSeconds = 0; }

    this.timer = <CountdownTimer>{
      seconds: this.timeInSeconds,
      runTimer: false,
      hasStarted: false,
      hasFinished: false,
      secondsRemaining: this.timeInSeconds
    };

    this.timer.displayTime = this.getSecondsAsDigitalClock(this.timer.secondsRemaining);
  }

  startWorkoutRound() {
    if (this.roundCount >= this.rounds) {
      console.log('Completed rounds: ' + this.roundCount);
      this.displayLabel = this.finishedMessage;
      this.timer.hasFinished = true;
      this.onRoundDone.emit({ round: this.roundCount });
    } else {
      this.displayLabel = this.workoutMessage;
      this.cssClass = "workout-round";
      this.timeInSeconds = this.roundTimeInSeconds;
      this.roundCount++;
      this.onRoundStarted.emit({ round: this.roundCount })
      this.timer.hasFinished = false;
      this.initTimer();
      this.startTimer(CountdownMode.Workout);
    }
  }

  startRestRound() {
    this.onRoundDone.emit({ round: this.roundCount });
    this.displayLabel = this.restMessage;
    this.cssClass = "rest-round";

    if (this.restInSeconds && this.restInSeconds > 0) {
      this.timeInSeconds = this.restInSeconds;

    } else if (this.restEveryXRoundsFor && this.restEveryXRoundsFor > 0) {
      this.timeInSeconds = this.restEveryXRoundsFor;
    }

    this.timer.hasFinished = false;
    this.initTimer();
    this.startTimer(CountdownMode.Rest);
  }

  startTimer(mode: CountdownMode) {

    switch (mode) {
      case CountdownMode.Stop: {
        this.setCountdownMode(CountdownMode.Stop);
        break;
      }
      case CountdownMode.Rest: {
        this.setCountdownMode(CountdownMode.Rest);
        break;
      }
      case CountdownMode.Workout: {
        this.setCountdownMode(CountdownMode.Workout);
        break;
      }
      case CountdownMode.Starting: {
        this.setCountdownMode(CountdownMode.Starting);
        this.timeInSeconds = this.startCountdownInSecs;
        this.displayLabel = this.startingMessage;
        this.cssClass = "warning";
        console.log('starting mode');
        this.onStartCountdown.emit();
        break;
      }

      default: {
        console.log('default: no sound');
        break;
      }
    }

    this.timer.hasStarted = true;
    this.timer.runTimer = true;
    this.timerTick();
  }

  pauseTimer() {
    this.timer.runTimer = false;
    this.paused = true;
  }

  resumeTimer() {
    this.paused = false;
    this.startTimer(this.workoutMode);
  }

  timerTick() {
    setTimeout(() => {
      if (!this.timer.runTimer) { return; }
      this.timer.secondsRemaining--;
      if (this.workoutMode == CountdownMode.Rest && this.restWarningSoundsAt && this.restWarningSoundsAt.indexOf(this.timer.secondsRemaining) != -1) {
        this.cssClass = "warning";
        this.displayLabel = this.warnMessage;
        this.playAudio('rest-warning');
        console.log('rest warning!!!');
        this.setCountdownMode(CountdownMode.Warning);
        this.onWarnStarted.emit({ nextRound: this.roundCount });
      } else if (this.workoutMode == CountdownMode.Workout && this.roundStatusSoundsAt && this.roundStatusSoundsAt.indexOf(this.timer.secondsRemaining) != -1) {
        this.playAudio('round-status');
      } else if (this.roundEndingWarningAt && this.workoutMode == CountdownMode.Workout && this.roundEndingWarningAt == this.timer.secondsRemaining) {
        let isNextRoundRestRound = this.roundCount % this.restEveryXRounds == 0;
        this.onRoundEndingWarn.emit({ round: this.roundCount, isNextRoundRestRound: isNextRoundRestRound });
      } else if (this.roundEndingWarningAt && this.workoutMode == CountdownMode.Rest && this.roundEndingWarningAt == this.timer.secondsRemaining) {
        this.onRoundEndingWarn.emit({ round: this.roundCount, isNextRoundRestRound: false });
      }
      else if (this.workoutMode == CountdownMode.Workout && this.roundWarningSoundsAt && this.roundWarningSoundsAt.indexOf(this.timer.secondsRemaining) != -1) {
        this.playAudio('round-warning');
      } else if (this.workoutMode == CountdownMode.Starting && this.startCountdownSoundsAt && this.startCountdownSoundsAt.indexOf(this.timer.secondsRemaining) != -1) {
        this.playAudio('warning');
      }

      this.setClockDisplay();

      if (this.timer.secondsRemaining >= 0) {
        this.timerTick();
      } else {
        if (this.timer.secondsRemaining < 0) {
          this.timer.secondsRemaining = 0;
          this.setClockDisplay();
        }
        if (this.restEveryXRounds && this.workoutMode == CountdownMode.Workout && (this.roundCount % this.restEveryXRounds) == 0) {
          this.onRestStarted.emit();
          this.playAudio('round-end');
          this.startRestRound();
          this.setCountdownMode(CountdownMode.Rest);
        } else
          if (this.restInSeconds > 0 && this.workoutMode == CountdownMode.Workout) {
            //workout done
            if (this.roundCount >= this.rounds) {
              this.playAudio('round-end');
              this.displayLabel = this.finishedMessage;
              this.timer.hasFinished = true;
              this.setCountdownMode(CountdownMode.Stop);
              this.onRoundDone.emit({ round: this.roundCount });
            } else { //resting...
              this.playAudio('round-end');
              this.startRestRound();
              this.setCountdownMode(CountdownMode.Rest);
            }
          } else { //working out
            this.playAudio('round-start');
            this.startWorkoutRound();
          }
      }
    }, 1000);
  }

  setCountdownMode(toMode: CountdownMode) {
    this.onCountdownModeChange.emit({ from: this.workoutMode, to: toMode });
    this.workoutMode = toMode;
  }

  playAudio(key) {
    this.audio.play(key);
  }

  setClockDisplay() {
    this.timer.displayTime = this.getSecondsAsDigitalClock(this.timer.secondsRemaining);
    this.percent = this.timer.secondsRemaining / this.timer.seconds * 100;
    this.increment = 180 / 100;
    const progress = 'rotate(' + this.increment * this.percent + 'deg)';
    this.transform = this.sanitizer.bypassSecurityTrustStyle(progress);
    this.fixTransform = this.sanitizer.bypassSecurityTrustStyle(progress);
  }

  getSecondsAsDigitalClock(inputSeconds: number) {
    const secNum = parseInt(inputSeconds.toString(), 10); // don't forget the second param
    const hours = Math.floor(secNum / 3600);
    const minutes = Math.floor((secNum - (hours * 3600)) / 60);
    const seconds = secNum - (hours * 3600) - (minutes * 60);
    let hoursString = '';
    let minutesString = '';
    let secondsString = '';
    hoursString = (hours < 10) ? '0' + hours : hours.toString();
    minutesString = (minutes < 10) ? '0' + minutes : minutes.toString();
    secondsString = (seconds < 10) ? '0' + seconds : seconds.toString();
    return minutesString + ':' + secondsString;
    // return hoursString + ':' + minutesString + ':' + secondsString;
  }

  /** If user leaves page, kill off timer */
  ngOnDestroy() {
    this.timer.hasFinished = true;
    this.timer.runTimer = false;

    console.log('killed timer');
  }
}
